/* eslint-disable import/prefer-default-export */
import { LocaleDictionary } from 'src/locales/LocaleDictionary'
import { useLocaleDictionary } from 'src/utils/useLocaleDictionary'

/**
 * Utility type to generate a union of all valid paths in a nested object type, represented as arrays of keys.
 *
 * This is used to strongly type the translation keys in the useTranslation hook.
 */
type PathTraversalKeys<T> = T extends string
  ? []
  : {
      [K in Extract<keyof T, string>]: [K, ...PathTraversalKeys<T[K]>]
    }[Extract<keyof T, string>]

/**
 * Utility type to join an array of strings with a specified delimiter.
 *
 * This is used to convert the array of keys generated by `PathTraversalKeys` into a dot notation string.
 */
type Join<T extends string[], D extends string> = T extends []
  ? never
  : T extends [infer F]
  ? F
  : T extends [infer F, ...infer R]
  ? F extends string
    ? `${F}${D}${Join<Extract<R, string[]>, D>}`
    : never
  : string

/**
 * Type representing all valid paths in the locale dictionary, in dot notation format.
 *
 * @example
 * "slices.mySlice.errorMessage" | "components.myComponent.label1"
 */
export type LocaleDictionaryPaths = Join<
  PathTraversalKeys<LocaleDictionary>,
  '.'
>

/**
 * Custom hook to provide translation functionality based on the current locale.
 */
export const useTranslation = () => {
  // The dictionary is typed as a generic Record to allow dynamic key access.
  const dictionary: Record<string, any> = useLocaleDictionary()

  // The translation function that takes a dot notation path and returns the corresponding localized string.
  const t = (path: LocaleDictionaryPaths): string => {
    const pathKeys = path.split('.')

    // Traverse over the dictionary using the keys derived from the dot notation path.
    const result = pathKeys.reduce((currentScope, currentKey) => {
      if (
        currentScope &&
        typeof currentScope === 'object' &&
        currentKey in currentScope
      ) {
        return currentScope[currentKey]
      }
      return undefined
    }, dictionary)

    // If the result is a string, return it; otherwise, return an empty string.
    return typeof result === 'string' ? result : ''
  }

  return { t }
}
